<template>
  <div>
    <Title title="Sign Up"></Title>
    <Header />
    <v-snackbar v-model="snackbar" bottom left :timeout="4000">{{ snackbarText }}</v-snackbar>
    <v-container class="form-body mt-12">
      <v-row class="mt-6">
        <v-col cols="12">
          <h2>Become part of the ISM project today.</h2>
        </v-col>
      </v-row>

      <v-form ref="signupForm">
        <p class="pt-5 mb-1 steps">
          Step
          <b>1</b> of
          <b>3</b>
        </p>
        <h2 class="mb-1">Fill in the centre details.</h2>
        <p>Centre details won't be able to change later.</p>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              ref="firstInput"
              v-model="name"
              label="Centre Name"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="addressLine1"
              label="Address Line 1 "
              :rules="emptyRule"
              class="my-3"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field v-model="addressLine2" label="Address Line 2 (Optional)" class="my-3"></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field v-model="addressLine3" label="Address Line 3 (Optional)" class="my-3"></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field v-model="city" label="City" class="my-3" :rules="emptyRule"></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field v-model="postcode" label="Post Code" :rules="emptyRule" class="my-3"></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-select
              v-model="country"
              :items="countries"
              label="Country"
              :rules="emptyRule"
              class="my-3"
            ></v-select>
          </v-col>
        </v-row>

        <p class="pt-12 mb-1 steps">
          Step
          <b>2</b> of
          <b>3</b>
        </p>
        <h2 class="mb-1">Provide a contact person.</h2>
        <p>This person will act as the main contact person.</p>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactFirstName"
              label="First Name"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactMiddleName"
              label="Middle Name (Optional)"
              class="my-3"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactLastName"
              label="Last Name"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactEmail"
              label="Email Address"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactTelephone"
              label="Telephone"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-text-field
              v-model="mainContactMobile"
              label="Mobile"
              class="my-3"
              :rules="emptyRule"
            ></v-text-field>
          </v-col>
        </v-row>

        <p class="pt-12 mb-1 steps">
          Step
          <b>3</b> of
          <b>3</b>
        </p>
        <h2 class="mb-1">Choose your preferred awarding organisation.</h2>
        <p>Different awarding organisations have different units and qualifications.</p>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-select
              v-model="awardingOrganisation"
              :items="aoNames"
              label="Awarding Organisation"
              :rules="emptyRule"
              class="my-3"
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" class>
            <vue-recaptcha :sitekey="siteKey" :loadRecaptchaScript="true" @verify="onVerifyCaptcha"></vue-recaptcha>
            <p class="error-feedback" v-if="showRecaptchaError">This field is required</p>

            <v-btn
              class="mt-12"
              tile
              color="primary"
              id="submit-btn"
              @click="onContinueSignup()"
            >Submit</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
import VueRecaptcha from "vue-recaptcha";

import Header from "../components/Header";
import Title from "../components/Title";

export default {
  mounted: function() {
    this.$refs.firstInput.focus();
    this.getCountries();
    this.getAwardingOrganisations();
  },
  components: {
    Header,
    VueRecaptcha,
    Title
  },
  data: () => {
    return {
      siteKey: "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI",
      recaptchaResponse: null,
      countries: [],
      awardingOrganisations: [],
      name: null,
      ukprn: null,
      addressLine1: null,
      addressLine2: null,
      addressLine3: null,
      city: null,
      postcode: null,
      country: null,
      awardingOrganisation: null,
      mainContactFirstName: null,
      mainContactMiddleName: null,
      mainContactLastName: null,
      mainContactEmail: null,
      mainContactTelephone: null,
      mainContactMobile: null,
      emptyRule: [v => !!v || "This field is required"],
      snackbar: false,
      snackbarText: "",
      showRecaptchaError: false
    };
  },
  computed: {
    aoNames: function() {
      const awardingOrganisations = this.awardingOrganisations.map(
        ao => ao.name
      );
      return awardingOrganisations;
    }
  },
  methods: {
    getCountries: async function() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/countries`
        );

        const data = await response.json();
        this.countries = data.countries.map(country => country.name);
      } catch (err) {
        console.log(err);
      }
    },

    onContinueSignup: async function() {
      if (!this.isFormValidate()) {
        return;
      }

      if (!this.isRecapctchaChecked(this.recaptchaResponse)) {
        this.showRecaptchaError = true;
        return;
      }

      try {
        this.$store.commit("switchProgressStatus", true);
        const response = await fetch(
          `${process.env.VUE_APP_ROOT_API}/centres`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(this.centreItem())
          }
        );

        const data = await response.json();
        this.snackbarText = data.message;
        this.snackbar = true;
        this.$store.commit("switchProgressStatus", false);
      } catch (err) {
        console.log(err);
      }
    },

    isFormValidate: function() {
      return this.$refs.signupForm.validate();
    },

    centreItem: function() {
      return {
        name: this.name,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        addressLine3: this.addressLine3,
        city: this.city,
        postcode: this.postcode,
        country: this.country,
        awardingOrganisation: this.getAoIdByName(this.awardingOrganisation),
        mainContactFirstName: this.mainContactFirstName,
        mainContactMiddleName: this.mainContactMiddleName,
        mainContactLastName: this.mainContactLastName,
        mainContactEmail: this.mainContactEmail,
        mainContactTelephone: this.mainContactTelephone,
        mainContactMobile: this.mainContactMobile,
        status: "pending"
      };
    },

    getAoIdByName: function(aoName) {
      const ao = this.getAoNameAndId().find(ao => aoName == ao.name);

      return ao.id;
    },

    getAoNameAndId: function() {
      return this.awardingOrganisations.map(ao => {
        return { name: ao.name, id: ao._id };
      });
    },

    onVerifyCaptcha: function(response) {
      this.recaptchaResponse = response;
      this.showRecaptchaError = false;
    },

    isRecapctchaChecked: function(captchaResponse) {
      return captchaResponse;
    },

    getAwardingOrganisations: async function() {
      try {
        const response = await fetch(`${process.env.VUE_APP_ROOT_API}/ao`);
        const data = await response.json();

        this.awardingOrganisations = data.aos;
      } catch (err) {
        console.log(err);
      }
    }
  }
};
</script>

<style scoped>
* {
  background-color: #fdfdfd;
}

.form-body {
  width: 40%;
}

.steps {
  text-transform: uppercase;
}

#submit-btn {
  height: 45px;
  margin: 0 auto;
  display: block;
  font-size: 17px;
  font-weight: 100;
}

.error-feedback {
  color: red;
  font-size: 0.9rem;
}
</style>;
